'use client';

import React from 'react';
import useCartStore from '@/ui/store/cart/cartStore';
import useUser from '@/ui/providers/userProvider';
import { createPortal } from 'react-dom';
import { ArrowLongLeftIcon } from '@heroicons/react/24/outline';
import { useTranslations } from 'next-intl';
import { useIsHydrated } from '@/ui/hooks/useIsHydrated';
import { CART_PORTAL_TARGET_ID } from '@/utils/constants';
import { useTrackViewCart } from '@/ui/hooks/tracking/use-track-view-cart';
import { MathHelper } from '@/utils/math-helper';
import { useCrystallizeCartHydration } from '@/ui/hooks/use-crystallize-cart-hydration';
import { cartDrawerVariant } from '../CartItem/types/cartItemVariants';
import CartAddOnServices from '../CartAddOnServices';
import CartItemsList from '../CartItemsList';
import CartSummary from '../CartSummary';
import { CartTopBar } from '../CartTopBar';
import { CartEmptySection } from '../CartEmptySection';
import { BottomCheckoutButton } from '../BottomCheckoutButton';
import Drawer from '../../../shared/Drawer';

export default function CartDrawer() {
  const {
    items,
    isCartDisplayed,
    toggleCartDisplay,
    showCartUpselling,
    setShowCartUpselling,
    cartUpsellingData,
    isEmpty,
    getPriceVariant,
    getTotalAfterDiscount,
    lastAddedToCart,
    showAdditionalInformation,
    setShowAdditionalInformation,
    additionalInformationTiles,
    setActiveAdditionalInfoBoxId,
    resetCartDisplayTransient,
  } = useCartStore();

  const handleCloseExtraLayer = (show: boolean) => {
    setShowAdditionalInformation(show);
    if (!show) {
      setActiveAdditionalInfoBoxId(undefined);
    }
  };

  const { session } = useUser();
  const isHydrated = useIsHydrated();
  const t = useTranslations('FullScreenOverlay');

  // GA4 view_cart - fire when drawer is opened by user (cart icon), not after add-to-cart
  useTrackViewCart(items, {
    enabled: isCartDisplayed && !isEmpty(),
    currency: getPriceVariant()?.currency,
    value: MathHelper.round(getTotalAfterDiscount()),
    resetOnDisable: true, // Reset when drawer closes or cart becomes empty
    skipWhen: !!lastAddedToCart, // Skip if drawer opened as result of add-to-cart
  });

  // Shadow-call the Shop API for tax validation and mismatch logging (no UI impact)
  useCrystallizeCartHydration();

  if (!isHydrated || typeof document === 'undefined') {
    return null;
  }

  // In case chooser is open, open the cart drawer as a children of the chooser overlay to avoid focus trap otherwise append to the root
  const chooserPortalTarget = document.getElementById(CART_PORTAL_TARGET_ID);
  const portalTarget = chooserPortalTarget || document.body;
  // Only within the chooser flow does the cart sit on top of the (inert) chooser and need its own
  // top-left Back to the upselling step. A cart opened on a normal page uses "Continue Shopping".
  const isInChooser = !!chooserPortalTarget;

  return createPortal(
    <Drawer
      overlayClose={toggleCartDisplay}
      open={isCartDisplayed}
      rootClass="z-60"
      extraLayerShown={showAdditionalInformation}
      extraLayerData={additionalInformationTiles}
      setExtraLayerShown={handleCloseExtraLayer}
      afterLeave={resetCartDisplayTransient}
    >
      {showCartUpselling && cartUpsellingData ? (
        <CartAddOnServices addOnSections={cartUpsellingData} />
      ) : (
        <>
          {/* The chooser behind is inert while this drawer is open, freezing its top-left Back.
              When the user reached the cart via the cross-sell step, render our own Back (into
              document.body, on top of the inert layer) that returns to that upselling step. */}
          {isInChooser &&
            cartUpsellingData &&
            cartUpsellingData.length > 0 &&
            createPortal(
              <button
                type="button"
                data-testid="cart-top-left-back"
                onClick={() => setShowCartUpselling(true)}
                className="fixed left-4 top-3 z-[70] flex items-center gap-1 sm:left-6"
              >
                <ArrowLongLeftIcon className="h-5 w-5 text-primary-100" />
                {t('Back')}
              </button>,
              document.body,
            )}

          <div className="flex-1 overflow-y-auto pb-6 ">
            <div className="px-4 sm:px-6">
              <CartTopBar onBackClick={toggleCartDisplay} />

              {isEmpty() && <CartEmptySection session={session} />}
            </div>
            {!isEmpty() && (
              <div className="mt-8 border-t border-neutral-10">
                <CartItemsList cartItems={items} variant={cartDrawerVariant} />
              </div>
            )}
            {!isEmpty() && <CartSummary />}
          </div>

          {!isEmpty() && <BottomCheckoutButton />}
        </>
      )}
    </Drawer>,
    portalTarget,
  );
}
